import type { InvoiceCommandType } from "@/shared/invoices/invoiceCommands";

/**
 * D156 — the manager's password on the exception acts (founder 2026-10-15,
 * amending D27). The till's copy of the server's rule
 * (`manager-approval.policy.ts`), so the sheet opens BEFORE the command is
 * sent rather than after a refusal. The server decides for itself either way:
 * if the two ever disagree the till hears `RESTAURANT_APPROVAL_REQUIRED` and
 * opens the sheet then.
 */

/** The four acts a password may be asked over. */
export type ApprovalCommandType =
  | "voidItem"
  | "compItem"
  | "applyBillDiscount"
  | "refundInvoice";

/**
 * Is this command, with this payload, one of the covered acts? Every void,
 * every comp (the staff meal's included), a discount at the bill, and a refund
 * only when it returns food to stock. Removing a discount restores money and
 * is not covered.
 */
export function commandNeedsApproval(
  type: InvoiceCommandType,
  payload: Record<string, unknown>,
): type is ApprovalCommandType {
  switch (type) {
    case "voidItem":
    case "compItem":
    case "applyBillDiscount":
      return true;
    case "refundInvoice": {
      const returned = payload.returnToStockItemIds;
      return Array.isArray(returned) && returned.length > 0;
    }
    default:
      return false;
  }
}

/**
 * Must the till collect a password before sending? Only when the store's
 * switch is on AND the session does not hold `RESTAURANT_EXCEPTION_APPROVE` —
 * a manager or the owner at the till acts directly.
 */
export function approvalRequiredFor(input: {
  switchOn: boolean;
  holdsApprove: boolean;
  type: InvoiceCommandType;
  payload: Record<string, unknown>;
}): boolean {
  return (
    input.switchOn &&
    !input.holdsApprove &&
    commandNeedsApproval(input.type, input.payload)
  );
}
